import styled from '@emotion/styled';
import { useState, useMemo } from 'react';
import type { ReviewImage, Review } from '..';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2e2e2e;
  margin: 0;
`;

const ViewAllButton = styled.button`
  background: none !important;
  border: none !important;
  color: #737373 !important;
  font-size: 12px !important;
  cursor: pointer !important;
  padding: 0 !important;
  font-family: 'Plus Jakarta Sans', sans-serif !important;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100px;
  background: #f8f8f8;
  border-radius: 8px;
  margin-top: 8px;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #6e6e6e;
  text-align: center;
  margin: 0;
`;

const ImageGrid = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const ImageContainer = styled.div`
  flex: 0 0 100px;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

interface ReviewImageGalleryProps {
  images: ReviewImage[];
  onViewAll?: () => void;
  reviews?: Review[];
}

export const ReviewImageGallery = ({ images, onViewAll, reviews = [] }: ReviewImageGalleryProps) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const bottomSheetImages = useMemo(() => {
    return images.map((image) => {
      const review = reviews.find((review) => review.images?.some((reviewImage) => reviewImage.id === image.id));

      const defaultReview: Review = {
        id: image.id,
        rating: 5,
        title: '',
        content: '',
        author: {
          id: 'unknown',
          name: 'Unknown User',
          avatar: '',
        },
        verifiedPurchase: false,
        createdAt: new Date().toISOString(),
        likeCount: 0,
        skinType: [],
        skinConcern: [],
        images: [image],
      };

      return {
        url: image.url,
        review: review || defaultReview,
      };
    });
  }, [images, reviews]);

  if (!images || images.length === 0) {
    return (
      <Container>
        <Header>
          <Title>Customer Images</Title>
        </Header>
        <EmptyStateContainer>
          <EmptyStateText>No customer images yet</EmptyStateText>
        </EmptyStateContainer>
      </Container>
    );
  }

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsBottomSheetOpen(true);
  };

  return (
    <Container>
      <Header>
        <Title>Customer Images</Title>
        <ViewAllButton onClick={onViewAll}>View all</ViewAllButton>
      </Header>
      <ImageGrid>
        {images.map((image, index) => (
          <ImageContainer key={image.id} onClick={() => handleImageClick(index)}>
            <Image src={image.url} alt={image.alt} crossOrigin="anonymous" />
          </ImageContainer>
        ))}
      </ImageGrid>
      {/* Comment for now because there is a bug with the bottom sheet */}
      {/* <ImageBottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        images={bottomSheetImages}
        initialSlide={selectedImageIndex}
      /> */}
    </Container>
  );
};

export default ReviewImageGallery;
